import React, { useState, useEffect, useCallback } from 'react';
import type { ConversationDto } from '../../services/api/messageService';
import { messageService } from '../../services/api/messageService';
import { chatSignalRService } from '../../services/signalr/ChatSignalRService';
import { safetyService, type ReportReason } from '../../services/api/safetyService';
import { ConversationList } from './ConversationList';
import { ChatWindow } from './ChatWindow';
import './ChatPage.css';

interface ChatPageProps {
  accessToken?: string;
  profileId?: string;
}

export const ChatPage: React.FC<ChatPageProps> = ({ accessToken, profileId }) => {
  const [conversations, setConversations] = useState<ConversationDto[]>([]);
  const [selectedMatchId, setSelectedMatchId] = useState<string | undefined>();
  const [isLoading, setIsLoading] = useState(true);
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth < 768);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load conversations
  const loadConversations = useCallback(async () => {
    try {
      const data = await messageService.getConversations();
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadConversations();
  }, [loadConversations]);

  // Connect to SignalR for real-time updates
  useEffect(() => {
    if (accessToken && profileId) {
      chatSignalRService.connect(accessToken, profileId).catch(console.error);

      chatSignalRService.setEventHandlers({
        onNewMessageNotification: (notification) => {
          // Update conversation list with new message preview
          setConversations(prev => {
            const updated = prev.map(conv => {
              if (conv.matchId === notification.matchId) {
                return {
                  ...conv,
                  lastMessage: {
                    id: '',
                    matchId: notification.matchId,
                    senderProfileId: conv.otherProfileId,
                    senderDisplayName: notification.senderName,
                    type: 'text' as const,
                    content: notification.preview,
                    sentAt: notification.sentAt,
                    isOwnMessage: false,
                  },
                  lastMessageAt: notification.sentAt,
                  unreadCount: conv.unreadCount + 1,
                };
              }
              return conv;
            });
            // Sort by last message time
            return updated.sort((a, b) => {
              const timeA = a.lastMessageAt || a.matchedAt;
              const timeB = b.lastMessageAt || b.matchedAt;
              return new Date(timeB).getTime() - new Date(timeA).getTime();
            });
          });
        },
        onReceiveMessage: (message) => {
          // Update conversation when receiving a message in the current chat
          setConversations(prev => {
            return prev.map(conv => {
              if (conv.matchId === message.matchId) {
                return {
                  ...conv,
                  lastMessage: message,
                  lastMessageAt: message.sentAt,
                  unreadCount: message.isOwnMessage ? conv.unreadCount :
                               selectedMatchId === conv.matchId ? 0 : conv.unreadCount + 1,
                };
              }
              return conv;
            }).sort((a, b) => {
              const timeA = a.lastMessageAt || a.matchedAt;
              const timeB = b.lastMessageAt || b.matchedAt;
              return new Date(timeB).getTime() - new Date(timeA).getTime();
            });
          });
        },
      });

      return () => {
        chatSignalRService.disconnect();
      };
    }
  }, [accessToken, profileId, selectedMatchId]);

  // Handle conversation selection
  const handleSelectConversation = (matchId: string) => {
    setSelectedMatchId(matchId);
    // Clear unread count for selected conversation
    setConversations(prev =>
      prev.map(conv =>
        conv.matchId === matchId ? { ...conv, unreadCount: 0 } : conv
      )
    );
  };

  // Handle back button on mobile
  const handleBack = () => {
    setSelectedMatchId(undefined);
  };

  // Safety handlers
  const handleUnmatch = async (matchId: string) => {
    const result = await safetyService.unmatch(matchId);
    if (result.success) {
      // Remove the conversation from the list
      setConversations(prev => prev.filter(c => c.matchId !== matchId));
      setSelectedMatchId(undefined);
    } else {
      console.error('Failed to unmatch:', result.error);
    }
  };

  const handleBlock = async (profileId: string) => {
    const result = await safetyService.blockUser(profileId);
    if (result.success) {
      // Remove conversations with this profile
      setConversations(prev => prev.filter(c => c.otherProfileId !== profileId));
      setSelectedMatchId(undefined);
    } else {
      console.error('Failed to block user:', result.error);
    }
  };

  const handleReport = async (profileId: string, reason: ReportReason, details?: string) => {
    const result = await safetyService.reportUser(profileId, reason, details);
    if (!result.success) {
      console.error('Failed to report user:', result.error);
    }
  };

  // Get selected conversation
  const selectedConversation = conversations.find(c => c.matchId === selectedMatchId);

  // Calculate total unread
  const totalUnread = conversations.reduce((sum, conv) => sum + conv.unreadCount, 0);

  if (isLoading) {
    return (
      <div className="chat-page loading">
        <div className="spinner"></div>
        <p>Loading conversations...</p>
      </div>
    );
  }

  return (
    <div className="chat-page">
      {/* Sidebar with conversation list */}
      <div className={`chat-sidebar ${isMobileView && selectedMatchId ? 'hidden' : ''}`}>
        <div className="chat-sidebar-header">
          <h2>Messages</h2>
          {totalUnread > 0 && (
            <span className="total-unread">{totalUnread}</span>
          )}
        </div>
        <ConversationList
          conversations={conversations}
          selectedMatchId={selectedMatchId}
          onSelectConversation={handleSelectConversation}
        />
      </div>

      {/* Main chat area */}
      <div className={`chat-main ${isMobileView && !selectedMatchId ? 'hidden' : ''}`}>
        {selectedConversation ? (
          <ChatWindow
            conversation={selectedConversation}
            onBack={isMobileView ? handleBack : undefined}
            onUnmatch={handleUnmatch}
            onBlock={handleBlock}
            onReport={handleReport}
          />
        ) : (
          <div className="chat-placeholder">
            <div className="placeholder-icon">
              <svg viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2zm0 14H6l-2 2V4h16v12z" />
              </svg>
            </div>
            <h3>Select a conversation</h3>
            <p>Choose a match from the list to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ChatPage;
